import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import PageHeader from '../components/PageHeader';
import { Card, Row, Col, Typography, Alert, Spin, Table, Tag, Button, Select, Input, Space, Tooltip, Badge, Drawer, Descriptions, Modal, Form, message } from 'antd';
import { AlertTriangle, Search, Filter, Eye, CheckCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { SearchOutlined, EyeOutlined, CheckCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, FilterOutlined } from '@ant-design/icons';
import crisisReportService from '../services/crisisReportService';
import { CRISIS_REPORT_STATUSES, CRISIS_REPORT_TYPES, CRISIS_REPORT_SEVERITIES } from '../types/crisisReport';
import { isCMTMember } from '../models/role';

const { Title, Text } = Typography;

/**
 * 🚨 Crisis Reports (Triage) Page
 * 
 * Main page for triaging crisis reports submitted from mobile app.
 * Displays real-time list of reports with filtering and action capabilities.
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Memoized filter functions and table columns
 * - useCallback for event handlers to prevent re-renders
 * - Real-time Firestore listeners with proper cleanup
 * - Efficient search filtering with useMemo
 * - Optimized table rendering with row keys
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Crisis Reports Triage Implementation
 */
const Reports = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Reports data and real-time listener
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  
  // Filters and search
  const [filters, setFilters] = useState({
    status: CRISIS_REPORT_STATUSES.SUBMITTED, // Default to submitted for triage
    type: '',
    search: ''
  });
  
  // Selected report for detail drawer
  const [selectedReport, setSelectedReport] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  
  // Action dialogs state
  const [acknowledgeModalVisible, setAcknowledgeModalVisible] = useState(false);
  const [promoteModalVisible, setPromoteModalVisible] = useState(false);
  const [declineModalVisible, setDeclineModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form instances
  const [promoteForm] = Form.useForm();
  const [declineForm] = Form.useForm();

  // Initialize page
  useEffect(() => {
    if (!currentUser?.orgId) {
      setError('No organisation ID found. Please try logging out and back in.');
      setLoading(false);
      return;
    }

    console.log('🚨 Reports page initialized for org:', currentUser.orgId);
    setLoading(false);
  }, [currentUser]);

  // Real-time listener for crisis reports
  useEffect(() => {
    if (!currentUser?.orgId) {
      setReports([]);
      setReportsLoading(false);
      return;
    }

    console.log('🚨 Setting up reports listener with filters:', filters);
    setReportsLoading(true);

    const unsubscribe = crisisReportService.subscribeToReports(
      currentUser.orgId,
      { 
        status: filters.status || undefined,
        type: filters.type || undefined
      },
      (reportsData, error) => {
        if (error) {
          console.error('🚨 Reports listener error:', error);
          setError(`Failed to load reports: ${error.message}`);
          setReports([]);
        } else {
          console.log('🚨 Reports data updated:', reportsData?.length || 0, 'reports');
          setReports(reportsData || []);
          setError(null);
        }
        setReportsLoading(false);
      }
    );

    return () => {
      console.log('🚨 Cleaning up reports listener');
      unsubscribe();
    };
  }, [currentUser?.orgId, filters.status, filters.type]);

  // Memoized filter function for performance
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (!filters.search) return true;
      
      const searchTerm = filters.search.toLowerCase();
      return (
        report.summary?.toLowerCase().includes(searchTerm) ||
        report.details?.toLowerCase().includes(searchTerm) ||
        report.reporterName?.toLowerCase().includes(searchTerm) ||
        report.reporterEmail?.toLowerCase().includes(searchTerm) ||
        report.siteName?.toLowerCase().includes(searchTerm) ||
        report.siteId?.toLowerCase().includes(searchTerm) ||
        report.type?.toLowerCase().includes(searchTerm) ||
        report.shortRef?.toLowerCase().includes(searchTerm)
      );
    });
  }, [reports, filters.search]);

  // Memoized filter change handler
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Memoized view report handler
  const handleViewReport = useCallback((report) => {
    setSelectedReport(report);
    setDrawerVisible(true);
  }, []);

  // Handle acknowledge report
  const handleAcknowledge = useCallback(async () => {
    if (!selectedReport) return;
    
    try {
      setActionLoading(true);
      await crisisReportService.acknowledgeReport(selectedReport.id, currentUser.orgId, currentUser.uid);
      message.success('Report acknowledged successfully');
      setAcknowledgeModalVisible(false);
      setDrawerVisible(false);
    } catch (error) {
      console.error('Error acknowledging report:', error);
      message.error(`Failed to acknowledge report: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  }, [selectedReport, currentUser.uid]);

  // Handle promote report - navigate to New Incident form with pre-filled data
  const handlePromote = useCallback(() => {
    if (!selectedReport) return;
    
    console.log('🔍 PROMOTE GUARD CHECK:', {
      reportId: selectedReport.id,
      currentStatus: selectedReport.status,
      promotedAt: selectedReport.promotedAt,
      linkedIncidentId: selectedReport.linkedIncidentId
    });
    
    // Enhanced guard against double-promotion
    if (selectedReport.status === 'promoted' || selectedReport.promotedAt || selectedReport.linkedIncidentId) {
      message.warning('This report has already been promoted and cannot be promoted again.');
      console.log('🚫 PROMOTION BLOCKED - Report already promoted:', {
        id: selectedReport.id,
        status: selectedReport.status,
        promotedAt: selectedReport.promotedAt,
        linkedIncidentId: selectedReport.linkedIncidentId
      });
      return;
    }
    
    console.log('🚀 Promoting crisis report to New Incident form:', selectedReport.id);
    
    // Close modals/drawers
    setPromoteModalVisible(false);
    setDrawerVisible(false);
    
    // Navigate to New Incident form with crisis report data
    navigate('/incidents/new', { 
      state: { 
        fromReportId: selectedReport.id,
        crisisReport: selectedReport 
      } 
    });
  }, [selectedReport, navigate]);

  // Handle decline report
  const handleDecline = useCallback(async (values) => {
    if (!selectedReport) return;
    
    try {
      setActionLoading(true);
      await crisisReportService.declineReport(
        selectedReport.id, 
        currentUser.orgId,
        currentUser.uid, 
        values.reviewNote
      );
      message.success('Report declined successfully');
      setDeclineModalVisible(false);
      setDrawerVisible(false);
      declineForm.resetFields();
    } catch (error) {
      console.error('Error declining report:', error);
      message.error(`Failed to decline report: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  }, [selectedReport, currentUser.uid, declineForm]);

  // Close drawer and reset state
  const closeDrawer = useCallback(() => {
    setDrawerVisible(false);
    setSelectedReport(null);
    setAcknowledgeModalVisible(false);
    setPromoteModalVisible(false);
    setDeclineModalVisible(false);
    promoteForm.resetFields();
    declineForm.resetFields();
  }, [promoteForm, declineForm]);

  // Memoized helper functions for performance
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case CRISIS_REPORT_STATUSES.SUBMITTED:
        return 'red';
      case CRISIS_REPORT_STATUSES.ACKNOWLEDGED:
        return 'orange';
      case CRISIS_REPORT_STATUSES.PROMOTED:
        return 'green';
      case CRISIS_REPORT_STATUSES.DECLINED:
        return 'default';
      default:
        return 'default';
    }
  }, []);

  const getSeverityColor = useCallback((severity) => {
    switch (severity) {
      case CRISIS_REPORT_SEVERITIES.CRITICAL:
        return 'red';
      case CRISIS_REPORT_SEVERITIES.HIGH:
        return 'orange';
      case CRISIS_REPORT_SEVERITIES.MED:
        return 'yellow';
      case CRISIS_REPORT_SEVERITIES.LOW:
        return 'green';
      default:
        return 'default';
    }
  }, []);

  const getTypeDisplayName = useCallback((type) => {
    switch (type) {
      case CRISIS_REPORT_TYPES.FIRE:
        return 'Fire';
      case CRISIS_REPORT_TYPES.ACTIVE_THREAT:
        return 'Active Threat';
      case CRISIS_REPORT_TYPES.MEDICAL:
        return 'Medical Emergency';
      case CRISIS_REPORT_TYPES.FLOOD:
        return 'Flood';
      case CRISIS_REPORT_TYPES.BOMB_THREAT:
        return 'Bomb Threat';
      case CRISIS_REPORT_TYPES.KIDNAP:
        return 'Kidnap';
      case CRISIS_REPORT_TYPES.MAJOR_VEHICLE:
        return 'Major Vehicle Incident';
      default:
        return type || 'Unknown';
    }
  }, []);

  // Memoized table columns for performance
  const columns = useMemo(() => [
    {
      title: 'Ref',
      dataIndex: 'shortRef',
      key: 'shortRef',
      width: 80,
      render: (ref) => (
        <Text code style={{ fontSize: '12px' }}>
          {ref || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag color="blue" style={{ fontSize: '11px' }}>
          {getTypeDisplayName(type)}
        </Tag>
      )
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
      render: (summary) => (
        <Tooltip title={summary}>
          <Text style={{ fontSize: '13px' }}>
            {summary || 'No summary provided'}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Severity',
      dataIndex: 'severityGuess',
      key: 'severityGuess',
      width: 90,
      render: (severity) => (
        severity ? (
          <Tag color={getSeverityColor(severity)} style={{ fontSize: '11px' }}>
            {severity}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: '11px' }}>Unknown</Text>
        )
      )
    },
    {
      title: 'Reporter',
      dataIndex: 'reporterName',
      key: 'reporterName',
      width: 150,
      ellipsis: true,
      render: (name, record) => (
        <Tooltip title={`${name} (${record.reporterEmail})`}>
          <Text style={{ fontSize: '12px' }}>
            {name || 'Unknown'}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Site',
      dataIndex: 'siteName',
      key: 'siteName',
      width: 120,
      ellipsis: true,
      render: (siteName, record) => (
        <Tooltip title={siteName || record.siteId}>
          <Text style={{ fontSize: '12px' }}>
            {siteName || record.siteId || 'Unknown'}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: '11px' }}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Received',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (timestamp) => (
        <Text style={{ fontSize: '11px' }}>
          {formatTimestamp(timestamp)}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewReport(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ], [getTypeDisplayName, getSeverityColor, getStatusColor, formatTimestamp, handleViewReport]);

  // Check if user has access
  if (!currentUser?.orgId) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader title="Reports (Triage)" />
        <Alert
          message="Access Denied"
          description="You need to be part of an organisation to access crisis reports."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Check if user has CMT permissions
  const hasCMTPermissions = isCMTMember(currentUser?.role);
  
  if (!hasCMTPermissions) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader title="Reports (Triage)" />
        <Alert
          message="Insufficient Permissions"
          description="Only Crisis Management Team (CMT) members can access crisis reports triage. Please contact your administrator if you need access."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader title="Reports (Triage)" />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader title="Reports (Triage)" />
        <Alert
          message="Error Loading Reports"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader title="Reports (Triage)" />
      
      {/* Page Description */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <AlertTriangle size={24} color="#ff4d4f" style={{ marginRight: '12px' }} />
              <Title level={4} style={{ margin: 0 }}>Crisis Reports Triage</Title>
            </div>
            <Text type="secondary">
              Review and triage crisis reports submitted from the mobile app. 
              Reports can be acknowledged, promoted to full incidents, or declined based on your assessment.
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error Loading Crisis Reports"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Filters and Search */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Search reports..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={6} md={4}>
                <Select
                  placeholder="Status"
                  value={filters.status}
                  onChange={(value) => handleFilterChange('status', value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Select.Option value={CRISIS_REPORT_STATUSES.SUBMITTED}>
                    <Tag color="red" style={{ fontSize: '11px', margin: 0 }}>Submitted</Tag>
                  </Select.Option>
                  <Select.Option value={CRISIS_REPORT_STATUSES.ACKNOWLEDGED}>
                    <Tag color="orange" style={{ fontSize: '11px', margin: 0 }}>Acknowledged</Tag>
                  </Select.Option>
                  <Select.Option value={CRISIS_REPORT_STATUSES.PROMOTED}>
                    <Tag color="green" style={{ fontSize: '11px', margin: 0 }}>Promoted</Tag>
                  </Select.Option>
                  <Select.Option value={CRISIS_REPORT_STATUSES.DECLINED}>
                    <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>Declined</Tag>
                  </Select.Option>
                </Select>
              </Col>
              <Col xs={24} sm={6} md={4}>
                <Select
                  placeholder="Type"
                  value={filters.type}
                  onChange={(value) => handleFilterChange('type', value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Select.Option value={CRISIS_REPORT_TYPES.FIRE}>Fire</Select.Option>
                  <Select.Option value={CRISIS_REPORT_TYPES.ACTIVE_THREAT}>Active Threat</Select.Option>
                  <Select.Option value={CRISIS_REPORT_TYPES.MEDICAL}>Medical Emergency</Select.Option>
                  <Select.Option value={CRISIS_REPORT_TYPES.FLOOD}>Flood</Select.Option>
                  <Select.Option value={CRISIS_REPORT_TYPES.BOMB_THREAT}>Bomb Threat</Select.Option>
                  <Select.Option value={CRISIS_REPORT_TYPES.KIDNAP}>Kidnap</Select.Option>
                  <Select.Option value={CRISIS_REPORT_TYPES.MAJOR_VEHICLE}>Major Vehicle</Select.Option>
                </Select>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Space>
                  <Badge count={filteredReports.length} showZero>
                    <Text type="secondary">Total Reports</Text>
                  </Badge>
                  {reportsLoading && <Spin size="small" />}
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Reports Table */}
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <AlertTriangle size={20} color="#ff4d4f" />
                <span>Crisis Reports</span>
                <Badge count={filteredReports.length} showZero />
              </Space>
            }
            extra={
              <Space>
                <Text type="secondary">Real-time updates</Text>
                {reportsLoading && <Spin size="small" />}
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={filteredReports}
              rowKey="id"
              loading={reportsLoading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} reports`
              }}
              scroll={{ x: 1200 }}
              responsive={['md']}
              size="small"
              locale={{
                emptyText: (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <AlertTriangle size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                    <Title level={4} type="secondary">No Reports Found</Title>
                    <Text type="secondary">
                      {filters.search || filters.status || filters.type
                        ? 'No reports match your current filters.'
                        : 'No crisis reports have been submitted yet.'
                      }
                    </Text>
                  </div>
                )
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Report Detail Drawer */}
      <Drawer
        title={
          <Space>
            <AlertTriangle size={20} color="#ff4d4f" />
            <span>Crisis Report Details</span>
            {selectedReport && (
              <Tag color={getStatusColor(selectedReport.status)}>
                {selectedReport.status}
              </Tag>
            )}
          </Space>
        }
        width={720}
        open={drawerVisible}
        onClose={closeDrawer}
        extra={
          selectedReport?.status === CRISIS_REPORT_STATUSES.SUBMITTED && (
            <Space>
              <Button
                type="default"
                icon={<CheckCircleOutlined />}
                onClick={() => setAcknowledgeModalVisible(true)}
              >
                Acknowledge
              </Button>
              <Button
                type="primary"
                icon={<ArrowUpOutlined />}
                onClick={() => setPromoteModalVisible(true)}
              >
                Promote
              </Button>
              <Button
                danger
                icon={<ArrowDownOutlined />}
                onClick={() => setDeclineModalVisible(true)}
              >
                Decline
              </Button>
            </Space>
          )
        }
      >
        {selectedReport && (
          <div>
            {/* Basic Information */}
            <Card title="Basic Information" style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Reference">{selectedReport.shortRef || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color="blue">{getTypeDisplayName(selectedReport.type)}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Severity">
                  {selectedReport.severityGuess ? (
                    <Tag color={getSeverityColor(selectedReport.severityGuess)}>
                      {selectedReport.severityGuess}
                    </Tag>
                  ) : (
                    <Text type="secondary">Not specified</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(selectedReport.status)}>
                    {selectedReport.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Received" span={2}>
                  {formatTimestamp(selectedReport.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Occurred At" span={2}>
                  {selectedReport.occurredAt ? new Date(selectedReport.occurredAt).toLocaleString() : 'Not specified'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Crisis Details */}
            <Card title="Crisis Details" style={{ marginBottom: '16px' }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Summary">
                  <Text>{selectedReport.summary || 'No summary provided'}</Text>
                </Descriptions.Item>
                {selectedReport.details && (
                  <Descriptions.Item label="Details">
                    <Text>{selectedReport.details}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Reporter Information */}
            <Card title="Reporter Information" style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Name">{selectedReport.reporterName || 'Unknown'}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedReport.reporterEmail || 'Unknown'}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Location Information */}
            <Card title="Location Information" style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Site">{selectedReport.siteName || selectedReport.siteId || 'Unknown'}</Descriptions.Item>
                <Descriptions.Item label="Site ID">{selectedReport.siteId || 'Unknown'}</Descriptions.Item>
                {selectedReport.location && (
                  <>
                    <Descriptions.Item label="Coordinates">
                      {selectedReport.location.lat?.toFixed(6)}, {selectedReport.location.lng?.toFixed(6)}
                    </Descriptions.Item>
                    <Descriptions.Item label="GPS Accuracy">
                      {selectedReport.location.accuracy ? `${selectedReport.location.accuracy}m` : 'Unknown'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            {/* Action History */}
            {(selectedReport.acknowledgedBy || selectedReport.promotedByUid || selectedReport.declinedByUid) && (
              <Card title="Action History">
                <Descriptions column={1} size="small">
                  {selectedReport.acknowledgedBy && (
                    <Descriptions.Item label="Acknowledged">
                      By: {selectedReport.acknowledgedBy} at {formatTimestamp(selectedReport.acknowledgedAt)}
                    </Descriptions.Item>
                  )}
                  {selectedReport.promotedByUid && (
                    <Descriptions.Item label="Promoted">
                      By: {selectedReport.promotedByUid} at {formatTimestamp(selectedReport.promotedAt)}
                      {selectedReport.reviewNote && (
                        <div style={{ marginTop: '8px' }}>
                          <Text strong>Review Note: </Text>
                          <Text>{selectedReport.reviewNote}</Text>
                        </div>
                      )}
                    </Descriptions.Item>
                  )}
                  {selectedReport.declinedByUid && (
                    <Descriptions.Item label="Declined">
                      By: {selectedReport.declinedByUid} at {formatTimestamp(selectedReport.declinedAt)}
                      {selectedReport.reviewNote && (
                        <div style={{ marginTop: '8px' }}>
                          <Text strong>Review Note: </Text>
                          <Text>{selectedReport.reviewNote}</Text>
                        </div>
                      )}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* Acknowledge Modal */}
      <Modal
        title="Acknowledge Crisis Report"
        open={acknowledgeModalVisible}
        onOk={handleAcknowledge}
        onCancel={() => setAcknowledgeModalVisible(false)}
        confirmLoading={actionLoading}
        okText="Acknowledge"
      >
        <p>Are you sure you want to acknowledge this crisis report?</p>
        <p>This will mark the report as reviewed and acknowledged by you.</p>
      </Modal>

      {/* Promote Modal */}
      <Modal
        title="Promote to Incident"
        open={promoteModalVisible}
        onCancel={() => setPromoteModalVisible(false)}
        onOk={handlePromote}
        okText="Create New Incident"
        okButtonProps={{ type: 'primary' }}
      >
        <p>This will open the New Incident form pre-filled with data from this crisis report.</p>
        <p>You'll be able to review and edit all details before creating the incident.</p>
        {selectedReport && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px' }}>
            <Text strong>Report: </Text>{selectedReport.shortRef}<br/>
            <Text strong>Type: </Text>{selectedReport.type}<br/>
            <Text strong>Summary: </Text>{selectedReport.summary}
          </div>
        )}
      </Modal>

      {/* Decline Modal */}
      <Modal
        title="Decline Crisis Report"
        open={declineModalVisible}
        onOk={() => declineForm.submit()}
        onCancel={() => setDeclineModalVisible(false)}
        confirmLoading={actionLoading}
        okText="Decline Report"
        okButtonProps={{ danger: true }}
      >
        <p>Decline this crisis report if no action is needed.</p>
        <Form
          form={declineForm}
          layout="vertical"
          onFinish={handleDecline}
        >
          <Form.Item
            name="reviewNote"
            label="Review Note"
            rules={[
              { required: true, message: 'Please provide a review note explaining why this report is being declined.' },
              { min: 10, message: 'Review note must be at least 10 characters long.' }
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Explain why this report is being declined..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Reports;
